import React, {
  RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { TabSize } from '@ui/components/Tabs/TabItem/TabItem'
import { IconFCType } from '@ui/icons/types'

export interface ModalInstance {
  id: string
  close: () => void
  isClosing: boolean
  requestClose: () => void
  setRequestClose: (fn: (() => void) | null) => void
  beginClose: () => boolean
  contentRef: RefObject<HTMLDivElement | null>
  contentInnerRef: RefObject<HTMLDivElement | null>
  canClose: boolean
  setCanClose: (v: boolean) => void
  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  containerMode?: ModalContainerMode
  setContainerMode?: (m: ModalContainerMode) => void
  isInteractive: boolean
}

export type ModalContainerMode = 'fullScreen' | 'popup' | 'bottomSheet' | 'drawer'

export type ModalTabItem = {
  id: string
  label: string
  iconLeft?: IconFCType
  iconRight?: IconFCType
  alert?: boolean
}

type ModalTabsCtx = {
  items: ModalTabItem[]
  setItems: (items: ModalTabItem[]) => void
  activeId: string
  setActiveId: (id: string) => void
  size: TabSize
  setSize: (s: TabSize) => void
  variant: ModalTabsVariantType
  setVariant: (v: ModalTabsVariantType) => void
}

export type ModalTabsVariantType = 'tabs' | 'segmented-control' | undefined

const InstanceCtx = createContext<ModalInstance | null>(null)
const TabsCtx = createContext<ModalTabsCtx | null>(null)

export const useModalTabsOptional = () => useContext(TabsCtx)
export const useModalTabs = () => {
  const v = useContext(TabsCtx)
  if (!v) throw new Error('useModalTabs must be used within a ModalInstanceProvider')
  return v
}

export const ModalInstanceProvider: React.FC<
  React.PropsWithChildren<{
    value: Pick<ModalInstance, 'id' | 'close' | 'isClosing' | 'isInteractive'>
  }>
> = ({ value, children }) => {
  const [items, setItems] = useState<ModalTabItem[]>([])
  const [activeId, setActiveId] = useState('')
  const [size, setSize] = useState<TabSize>('md')
  const [variant, setVariant] = useState<ModalTabsVariantType>()

  const [canClose, setCanClose] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  const contentRef = useRef<HTMLDivElement>(null)
  const contentInnerRef = useRef<HTMLDivElement | null>(null)

  const [containerMode, setContainerMode] = useState<ModalContainerMode | undefined>(undefined)

  const requestCloseRef = useRef<(() => void) | null>(null)
  const requestClose = useCallback(() => {
    ;(requestCloseRef.current ?? value.close)()
  }, [value.close])

  const setRequestClose = useCallback((fn: (() => void) | null) => {
    requestCloseRef.current = fn
  }, [])

  const closeRequestedRef = useRef(false)

  useEffect(() => {
    if (!value.isClosing) closeRequestedRef.current = false
  }, [value.isClosing])

  const beginClose = useCallback(() => {
    if (value.isClosing) return false
    if (closeRequestedRef.current) return false
    closeRequestedRef.current = true
    return true
  }, [value.isClosing])

  const instanceValue = useMemo(
    () => ({
      ...value,
      requestClose,
      setRequestClose,
      beginClose,
      contentRef,
      contentInnerRef,
      canClose,
      setCanClose,
      isLoading,
      setIsLoading,
      containerMode,
      setContainerMode,
    }),
    [value, requestClose, setRequestClose, beginClose, canClose, isLoading, containerMode]
  )

  const tabsValue = useMemo(
    () => ({
      items,
      setItems,
      activeId,
      setActiveId,
      size,
      setSize,
      variant,
      setVariant,
    }),
    [items, activeId, size, variant]
  )

  return (
    <InstanceCtx.Provider value={instanceValue}>
      <TabsCtx.Provider value={tabsValue}>{children}</TabsCtx.Provider>
    </InstanceCtx.Provider>
  )
}

export const useModalInstance = () => {
  const ctx = useContext(InstanceCtx)
  if (!ctx) throw new Error('useModalInstance must be used within a ModalInstanceProvider')
  return ctx
}
export const useModalInstanceOptional = () => {
  return useContext(InstanceCtx)
}
